// apps/fe/src/modules/catalog/hooks/use-profile-catalog-data.ts

'use client';
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useAuthStore, useAuthenticatedFetch } from '@/auth';
import { createAuthenticatedCatalogServices } from '../services/api-services-authenticated';
import { CategoryTypes } from '../types/catalog-category-data';
import { EventBus } from '@/shared/utils';
import { CatalogEvents, CatalogEventKeys } from '../events';
import { ICourseData } from '../types/course-data';
import { CourseDataFilters } from '../types/filtered-data';
import {
  userProfileDataCache,
  invalidateFavoriteCaches,
  invalidateCaches,
} from '../utils/cache-utils';

/**
 * Hook for fetching and managing user-specific catalog data.
 * Handles SSG data rehydration with user-specific information.
 */
export function useProfileCatalogData({
  dataType, // 'view', 'category', 'search'
  initialData, // SSG data to enhance with user-specific info
  catalogParams = {}, // category name, search query, etc.
  eventBus,
}: {
  dataType: 'view' | 'category' | 'search';
  initialData: any;
  catalogParams?: Record<string, any>;
  eventBus?: EventBus<CatalogEvents>;
}) {
  const { user } = useAuthStore();
  const { fetchWithAuth } = useAuthenticatedFetch();

  // For authenticated users, start with null data to force loading state
  // For guest users, immediately use initialData
  const [data, setData] = useState<any>(user ? null : initialData);

  // Start with loading=true for authenticated users only
  const [loading, setLoading] = useState(!!user);

  // For guest users, SSG data is already considered "user data"
  // For authenticated users, we need to fetch it
  const [hasUserData, setHasUserData] = useState(!user);

  const [error, setError] = useState<Error | null>(null);
  const [preservedFilters, setPreservedFilters] = useState<CourseDataFilters | null>(null);

  // Refs to track component state
  const fetchingRef = useRef<boolean>(false);
  const fetchCompletedRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Flag to track retry attempts
  const retryCountRef = useRef<number>(0);
  const MAX_RETRIES = 2;

  // Properly memoize catalog params to prevent unnecessary recreations
  const stableCatalogParams = useMemo(
    () => ({
      categoryName: catalogParams.categoryName,
      query: catalogParams.query,
      courseTypes: catalogParams.courseTypes,
    }),
    [catalogParams.categoryName, catalogParams.query, catalogParams.courseTypes],
  );

  // Create authenticated services with token refresh - memoized
  const authServices = useMemo(
    () => createAuthenticatedCatalogServices(fetchWithAuth, eventBus),
    [fetchWithAuth, eventBus],
  );

  // Create a cache key for this data request
  const cacheKey = useMemo(() => {
    return JSON.stringify({
      dataType,
      userEmail: user?.email,
      params: stableCatalogParams,
    });
  }, [dataType, user?.email, stableCatalogParams]);

  // Check if this request should refetch data or can use cached/existing data
  const shouldRefetchData = useCallback(
    (filters?: CourseDataFilters) => {
      // Skip refetch if we already have data and there are no significant filter changes
      return !hasUserData || !data;
    },
    [hasUserData, data],
  );

  // Listen for navigation events to handle cache invalidation
  useEffect(() => {
    const handleLeaveSection = () => {
      // Optionally clear caches when leaving catalog section
      // This is a balance between performance and freshness
      if (user?.email) {
        console.log('Leaving catalog section, invalidating user caches for', user.email);
        invalidateCaches({ userProfile: true });
      }
    };

    // We could listen for navigation events, but they need to be emitted by the router
    // For now, we'll rely on component unmount to clear caches

    return () => {
      // Clear caches when component unmounts
      if (user?.email) {
        console.log('Catalog component unmounting, considering cache invalidation');
        // Optionally: invalidateCaches({ userProfile: true });
      }
    };
  }, [user?.email]);

  // Add a function to update favorites in the dataset
  const updateFavoriteStatus = useCallback(
    (courseId: string, favouriteId: string | undefined) => {
      if (!hasUserData && !data && !initialData) return; // Verify if we have data to update

      // Determine which data source to update (enriched or initial)
      const dataToUpdate = hasUserData ? data : initialData;
      if (!dataToUpdate) return;

      setData((prevData: any) => {
        // Deep copy to avoid state mutations
        const newData = JSON.parse(JSON.stringify(prevData || dataToUpdate));

        // Helper to update a course in an array
        const updateCourseInArray = (courses: ICourseData[] = []) => {
          if (!Array.isArray(courses)) return [];

          return courses.map((course) => {
            if (course && course.documentId === courseId) {
              return {
                ...course,
                favouriteId: favouriteId,
                isFavourite: !!favouriteId,
              };
            }
            return course;
          });
        };

        // Update based on data structure for each view type
        try {
          if (dataType === 'view' && newData?.categories) {
            // For the home catalog view
            Object.keys(newData.categories).forEach((categoryKey: string) => {
              const category = newData.categories[categoryKey];
              if (category && category.items && Array.isArray(category.items)) {
                category.items = updateCourseInArray(category.items);
              }
            });
            return newData;
          } else if (dataType === 'category' && newData?.category?.courses) {
            // For the category view
            newData.category.courses = updateCourseInArray(newData.category.courses);
            return newData;
          } else if (dataType === 'search' && Array.isArray(newData)) {
            // For the search view
            return updateCourseInArray(newData);
          }
        } catch (e) {
          console.error('Error updating favorite status:', e);
        }

        // If data structure not recognized, return data unchanged
        return prevData;
      });

      // Set hasUserData to true to indicate that data has been customized
      if (!hasUserData) {
        setHasUserData(true);
      }

      // Invalidate favorite-related caches
      if (user?.email) {
        invalidateFavoriteCaches(user.email, dataType);
      }
    },
    [dataType, hasUserData, data, initialData, user?.email],
  );

  // Fetch function implementation
  async function doFetchUserData(email: string) {
    // Skip if already fetching or completed
    if (fetchingRef.current || fetchCompletedRef.current || !mountedRef.current) {
      return;
    }

    // Check cache first
    if (userProfileDataCache.has(cacheKey)) {
      const cachedData = userProfileDataCache.get(cacheKey);
      setData(cachedData);
      setHasUserData(true);
      setLoading(false);
      fetchCompletedRef.current = true;
      return;
    }

    // Cancel any previous requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Mark that a fetch is in progress
    fetchingRef.current = true;
    setLoading(true);

    // Don't reset error if retrying, to maintain UI consistency
    if (retryCountRef.current === 0) {
      setError(null);
    }

    try {
      let result;

      // Use the appropriate service based on data type
      switch (dataType) {
        case 'view':
          result = await authServices.getUserCatalogView(email);
          break;

        case 'search':
          result = await authServices.userSearch(
            email,
            stableCatalogParams.query,
            stableCatalogParams.courseTypes,
          );
          break;
        default:
          throw new Error(`Invalid data type: ${dataType}`);
      }

      if (!mountedRef.current) return;

      if (!result.success) {
        throw result.error || new Error('Unknown error in profile data fetch');
      }

      // More robust data structure handling
      let processedData;

      if (result.data) {
        // Safely check if result.data has a 'data' property
        if (
          typeof result.data === 'object' &&
          result.data !== null &&
          'data' in result.data &&
          (typeof (result.data as any).data === 'object' ||
            Array.isArray((result.data as any).data))
        ) {
          processedData = (result.data as any).data;
        }
        // Otherwise use result.data directly
        else {
          processedData = result.data;
        }
      } else {
        // Fallback to initialData if no data
        processedData = initialData;
      }

      // Important: Preserve the filters from initialData
      if (typeof processedData === 'object' && processedData !== null && initialData.filters) {
        if (Array.isArray(processedData)) {
          // For search results (array), we can't add filters directly
        } else {
          // For home and category (object), we can add filters
          processedData.filters = initialData.filters;
        }
      }

      // Cache the processed data
      userProfileDataCache.set(cacheKey, processedData);

      // Set the enhanced data with user info
      setData(processedData);
      setHasUserData(true);
      setError(null);
      retryCountRef.current = 0; // Reset retry counter on success

      // Mark fetch as completed
      fetchCompletedRef.current = true;
    } catch (err) {
      console.error('Error fetching user catalog data:', err);
      if (mountedRef.current) {
        const errorObj = err instanceof Error ? err : new Error(String(err));
        setError(errorObj);

        // Increment retry counter and retry if we haven't reached the max
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++;
          // Retry after a short delay
          setTimeout(() => {
            // We must not set fetchCompletedRef to false here
            // because we'll keep trying
            fetchingRef.current = false;
            if (user?.email && mountedRef.current) {
              doFetchUserData(user.email);
            }
          }, 2000); // 2 second delay before retrying
        } else {
          // If we've exhausted retries, use initial data
          console.log('Max retries reached, using initial data');
          // Mark as having user data to prevent new attempts
          setHasUserData(true);
          setData(initialData);
          // Mark fetch as completed even though it failed
          fetchCompletedRef.current = true;
        }
      }
    } finally {
      if (mountedRef.current) {
        // Only if we've reached max retries or succeeded
        if (retryCountRef.current >= MAX_RETRIES || fetchCompletedRef.current) {
          setLoading(false);
          fetchingRef.current = false;
        }
      }
    }
  }

  // Memoize the fetch function interface
  const fetchUserData = useCallback(() => {
    if (user?.email && !fetchCompletedRef.current && mountedRef.current) {
      doFetchUserData(user.email);
    }
  }, [user?.email, cacheKey]);

  // Listen for favorite events to keep data in sync across the application
  useEffect(() => {
    if (!eventBus || !user) return;

    // Handle all favorite success events
    const handleFavoriteCreate = (data: { documentId: string }) => {
      if (data && data.documentId) {
        console.log('Global favorite create event:', data);
        // Invalidate favorite-related caches
        if (user?.email) {
          invalidateFavoriteCaches(user.email);
        }
        setHasUserData(true); // Ensure we're using the enriched data
      }
    };

    const handleFavoriteDelete = () => {
      console.log('Global favorite delete event');
      // Invalidate favorite-related caches
      if (user?.email) {
        invalidateFavoriteCaches(user.email);
      }
      setHasUserData(true); // Ensure we're using the enriched data
    };

    // Register listeners for all course types
    const courseTypes = ['serie', 'masterclass', 'learningPath'];
    courseTypes.forEach((type) => {
      const createKey =
        CatalogEventKeys.favourite.create[type as 'serie' | 'masterclass' | 'learningPath'].success;
      const deleteKey =
        CatalogEventKeys.favourite.delete[type as 'serie' | 'masterclass' | 'learningPath'].success;

      eventBus.on(createKey, handleFavoriteCreate);
      eventBus.on(deleteKey, handleFavoriteDelete);
    });

    return () => {
      // Clean up listeners
      courseTypes.forEach((type) => {
        const createKey =
          CatalogEventKeys.favourite.create[type as 'serie' | 'masterclass' | 'learningPath']
            .success;
        const deleteKey =
          CatalogEventKeys.favourite.delete[type as 'serie' | 'masterclass' | 'learningPath']
            .success;

        eventBus.off(createKey, handleFavoriteCreate);
        eventBus.off(deleteKey, handleFavoriteDelete);
      });
    };
  }, [eventBus, user]);

  // Use effect with minimal dependencies to prevent unnecessary runs
  useEffect(() => {
    mountedRef.current = true;
    retryCountRef.current = 0; // Reset retry counter on mount or dep change

    // Only fetch on initial mount or when user changes
    if (user?.email && !fetchCompletedRef.current) {
      fetchUserData();
    }

    // Clean up on unmount
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [user?.email, fetchUserData]);

  // Expose a refetch function that resets the completion flag
  const manualRefetch = useCallback(async () => {
    // Reset the completion flag for manual refetch
    fetchCompletedRef.current = false;
    retryCountRef.current = 0;

    // Clear cache for this request
    userProfileDataCache.delete(cacheKey);

    if (user?.email) {
      doFetchUserData(user.email);
    }
  }, [user?.email, cacheKey]);

  // Add method to handle filter state updates from useBaseFilter
  const setActiveFilters = useCallback(
    (filters: CourseDataFilters) => {
      setPreservedFilters(filters);

      // Save filters to localStorage for persistence
      try {
        const storageKey = `${dataType}_filters_${stableCatalogParams.categoryName || 'default'}`;
        localStorage.setItem(storageKey, JSON.stringify(filters));
      } catch (e) {
        console.warn('Failed to save filters to localStorage:', e);
      }
    },
    [stableCatalogParams.categoryName, dataType],
  );

  // Load saved filters on initialization - only once
  useEffect(() => {
    if (!preservedFilters) {
      try {
        const storageKey = `${dataType}_filters_${stableCatalogParams.categoryName || 'default'}`;
        const savedFilters = localStorage.getItem(storageKey);
        if (savedFilters) {
          setPreservedFilters(JSON.parse(savedFilters));
        }
      } catch (e) {
        console.warn('Failed to load filters from localStorage:', e);
      }
    }
  }, [dataType, stableCatalogParams.categoryName]);

  return {
    data,
    loading,
    error,
    hasUserData,
    refetch: manualRefetch,
    setData,
    updateFavoriteStatus,
    preservedFilters,
    setActiveFilters,
    shouldRefetchData,
  };
}
